<template>
  <section class="motoboy-section mb-2">
    <div class="d-flex align-items-center justify-content-between mb-1">
      <h2 class="h3 mb-0">
        {{ $t('Motoboys') }}
        <b-badge variant="primary">
          {{ workingMotoboys.length }}
        </b-badge>
      </h2>
      <div class="d-flex align-items-center">
        <small class="text-muted mr-1">{{ $t('Presença e composição de entregas') }}</small>
        <e-button
          id="motoboy-section_add-presence"
          size="sm"
          icon="person-plus"
          :text="$t('Adicionar presença')"
          :disabled="loading"
          @click="showPresenceSidebar = true"
        />
      </div>
    </div>

    <div
      v-if="loading"
      class="py-2"
    >
      <e-spinner />
    </div>
    <b-alert
      v-else-if="!workingMotoboys.length"
      show
      variant="light"
      class="mb-0"
    >
      {{
        motoboys.length
          ? $t('Nenhum Motoboy presente no momento')
          : $t('Não há Motoboys vinculados a esta loja')
      }}
    </b-alert>
    <div
      v-else
      class="motoboy-section__list"
    >
      <b-card
        v-for="motoboy in workingMotoboys"
        :key="motoboyId(motoboy)"
        class="motoboy-card mb-0"
        :class="`motoboy-card--${presenceState(motoboy).toLowerCase()}`"
        no-body
      >
        <div class="p-1">
          <div class="d-flex align-items-center justify-content-between">
            <strong>{{ motoboyName(motoboy) }}</strong>
            <b-badge :variant="presenceVariant(motoboy)">
              {{ presenceLabel(motoboy) }}
            </b-badge>
          </div>
          <div class="mt-1">
            <small class="font-weight-bold">{{ $t('Pedidos') }}:</small>
            <span
              v-if="!assignedOrders(motoboy).length"
              class="text-muted ml-50"
            >
              {{ $t('Nenhum pedido atribuído') }}
            </span>
            <b-badge
              v-for="order in assignedOrders(motoboy)"
              :key="orderId(order)"
              variant="light-primary"
              class="ml-50"
            >
              #{{ orderId(order) }}
            </b-badge>
          </div>

          <div class="d-flex mt-1">
            <template v-if="presenceState(motoboy) === 'Present'">
              <e-button
                size="sm"
                variant="outline-secondary"
                icon="person-x"
                :busy="isPending(motoboy)"
                :text="$t('Marcar ausente')"
                @click="changePresence(motoboy, 'Absent')"
              />
              <e-button
                v-if="assignedOrders(motoboy).length"
                class="ml-50"
                size="sm"
                icon="truck"
                :busy="isPending(motoboy)"
                :text="$t('Iniciar entrega')"
                @click="startDelivery(motoboy)"
              />
            </template>
            <e-button
              v-else-if="hasDeliveryInRoute(motoboy)"
              size="sm"
              icon="plus-circle"
              :text="$t('Adicionar pedido')"
              @click="openAddOrder(motoboy)"
            />
            <e-button
              v-else
              size="sm"
              icon="person-check"
              :busy="isPending(motoboy)"
              :text="$t('Liberar')"
              @click="changePresence(motoboy, 'Present')"
            />
          </div>
        </div>
      </b-card>
    </div>

    <motoboy-presence-sidebar
      :show="showPresenceSidebar"
      @hidden="showPresenceSidebar = false"
    />
    <motoboy-add-order-sidebar
      :show="!!addOrderMotoboy"
      :motoboy="addOrderMotoboy"
      @hidden="addOrderMotoboy = null"
    />
  </section>
</template>

<script>
import { BAlert, BBadge, BCard } from 'bootstrap-vue'
import { mapActions } from 'vuex'
import { EButton, ESpinner } from '@/views/components'
import MotoboyAddOrderSidebar from './MotoboyAddOrderSidebar.vue'
import MotoboyPresenceSidebar from './MotoboyPresenceSidebar.vue'
import motoboyPresence from './motoboy-presence'

export default {
  name: 'MotoboySection',

  components: {
    BAlert,
    BBadge,
    BCard,
    EButton,
    ESpinner,
    MotoboyAddOrderSidebar,
    MotoboyPresenceSidebar,
  },

  mixins: [motoboyPresence],

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showPresenceSidebar: false,
      addOrderMotoboy: null,
    }
  },

  computed: {
    // Só quem está trabalhando agora (presente ou em rota). Os ausentes ficam no sidebar
    // de presença, de onde a loja os marca presentes.
    workingMotoboys() {
      return this.motoboys.filter(motoboy => this.presenceState(motoboy) !== 'Absent')
    },
  },

  methods: {
    ...mapActions('pages/sale/deliveryDashboard', ['startMotoboyDelivery']),
    openAddOrder(motoboy) {
      this.addOrderMotoboy = motoboy
    },
    orderId(order) {
      return typeof order === 'object' ? order.id ?? order.saleId ?? order.orderId : order
    },
    async startDelivery(motoboy) {
      try {
        await this.startMotoboyDelivery(this.motoboyId(motoboy))
      } catch (error) {
        this.showError({ error })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.motoboy-section {
  padding: 1rem;
  border: 1px solid #d8dbe0;
  border-radius: 0.5rem;
  background: white;

  &__list {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.75rem;

    @media (max-width: 991px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 575px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}

.motoboy-card {
  border-left: 5px solid var(--secondary);

  &--present {
    border-left-color: var(--success);
  }

  &--unavailable {
    border-left-color: var(--warning);
  }
}
</style>
